import React, { useState, useEffect, useCallback, useRef } from "react";
import { debounce } from "lodash";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { usePendingEdit } from '../general/unsaved-changes-context';

/**
 * SyncStrategy:
 * 'change' - Updates the global state as the user types (debounced).
 * 'blur'   - Updates the global state only when the input loses focus.
 */
type SyncStrategy = 'change' | 'blur';

type BaseProps = {
  initialValue: string;
  onSync: (value: string) => void;
  isTextArea?: boolean;
  debounceMs?: number;
  syncStrategy?: SyncStrategy;
};

/**
 * We combine our custom props with standard HTML attributes 
 * so things like 'style', 'className', 'rows', and 'placeholder' work.
 */
type DebouncedInputProps = BaseProps & 
  React.ComponentPropsWithoutRef<"input"> & 
  React.ComponentPropsWithoutRef<"textarea">;

export const DebouncedInput = ({
  initialValue,
  onSync,
  isTextArea = false,
  debounceMs = 500,
  syncStrategy = 'change',
  ...rest
}: DebouncedInputProps) => {
  // 1. Local state for immediate typing feedback
  const [localValue, setLocalValue] = useState(initialValue);
  
  // 2. Track the value when focus started to prevent redundant syncs on blur
  const valueOnFocus = useRef(initialValue);

  // 3. Keep the onSync function in a Ref so the debounce doesn't reset 
  // if the parent component re-renders and changes the function identity.
  const onSyncRef = useRef(onSync);
  onSyncRef.current = onSync;

  // 4. Synchronize local state if the initialValue is changed externally
  // (e.g., when the user selects a different fragment from a list).
  useEffect(() => {
    setLocalValue(initialValue);
    valueOnFocus.current = initialValue;
  }, [initialValue]);

  // 5. Setup the debounce logic
  const debouncedSync = useCallback(
    debounce((nextValue: string) => {
      onSyncRef.current(nextValue);
    }, debounceMs),
    [debounceMs]
  );

  useEffect(() => () => debouncedSync.cancel(), [debouncedSync]);
  usePendingEdit({
    dirty: () => localValue !== initialValue,
    flush: () => {
      debouncedSync.cancel();
      if (localValue !== initialValue) onSyncRef.current(localValue);
      valueOnFocus.current = localValue;
    },
    cancel: () => debouncedSync.cancel(),
  });

  const handleFocus = (e: React.FocusEvent<any>) => {
    // Capture the state of the input the moment the user clicks in
    valueOnFocus.current = e.target.value;
    
    // Call the original onFocus if it was passed in props
    if (rest.onFocus) {
        rest.onFocus(e);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const val = e.target.value;
    setLocalValue(val);

    // If strategy is 'change', we trigger the debounced sync
    if (syncStrategy === 'change') {
      debouncedSync(val);
    }
  };

  const handleBlur = (e: React.FocusEvent<any>) => {
    // Cancel any pending debounced calls to prevent race conditions
    debouncedSync.cancel(); 
    
    // Only call onSync if the value has actually changed since focus
    if (localValue !== valueOnFocus.current) {
      onSync(localValue);
      // Update our baseline so a second blur doesn't trigger another sync
      valueOnFocus.current = localValue;
    }

    // Call the original onBlur if it was passed in props
    if (rest.onBlur) {
        rest.onBlur(e);
    }
  };

  // Common props shared by both InputText and InputTextarea
  const commonProps = {
    ...rest,
    value: localValue,
    onChange: handleChange,
    onBlur: handleBlur,
    onFocus: handleFocus,
  };

  // Render the appropriate PrimeReact component
  if (isTextArea) {
    return <InputTextarea {...commonProps} />;
  }

  return <InputText {...commonProps} />;
};
